import { Fragment, ReactElement } from 'react';

import { Disclosure as HeadlessDisclosure } from '@headlessui/react';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { BadgeCounter } from '@components/badge-counter';
import { IconProps } from '@components/icons';
import { IconDirectionDown } from '@components/icons/direction-down';
import { InfoTooltip } from '@components/info-tooltip';
import { theme } from '@styles/theme';

import { Stack } from '../stack';
import { Text } from '../text';

export interface DisclosureProps {
  icon?: ReactElement<IconProps>;
  info?: string;
  text: string;
  description?: string;
  content: ReactElement[];
  showContentActivity?: boolean;
  isDefaultOpen?: boolean;
  isDisabled?: boolean;
  iconLocation?: 'inFront' | 'nextToTitle'; // icon in front of title+decription or next to title
}

export function Disclosure(props: DisclosureProps) {
  const {
    icon,
    text,
    content,
    info,
    showContentActivity,
    description,
    isDefaultOpen = true,
    isDisabled = false,
    iconLocation = 'nextToTitle',
  } = props;
  return (
    <HeadlessDisclosure defaultOpen={isDefaultOpen}>
      {({ open }) => (
        <>
          <HeadlessDisclosure.Button as={Fragment} disabled={isDisabled}>
            <HeaderContainer>
              <Stack space={2} orientation="horizontal" align="center">
                {iconLocation === 'inFront' && icon}
                <div>
                  <Stack space={2} orientation="horizontal" align="center">
                    {iconLocation === 'nextToTitle' && icon}
                    <Text font="semibold14" color="textPrimary" text={text} />
                    {info && <InfoTooltip tooltip={info} />}
                  </Stack>
                  {description && (
                    <Text font="regular14" color="textSecondary" text={description} />
                  )}
                </div>
              </Stack>
              <Stack orientation="horizontal" space={2}>
                {showContentActivity && <BadgeCounter value={content.length} size="sm" />}
                <span className={open ? 'arrow down' : 'arrow right'}>
                  <IconDirectionDown color="elementPurple50" />
                </span>
              </Stack>
            </HeaderContainer>
          </HeadlessDisclosure.Button>
          {content.length > 0 && (
            <HeadlessDisclosure.Panel static>
              <Collapse isCollapsed={!open}>{content}</Collapse>
            </HeadlessDisclosure.Panel>
          )}
        </>
      )}
    </HeadlessDisclosure>
  );
}

const HeaderContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '--icon-color': theme.color.textPrimary,
  ':hover': {
    cursor: 'pointer',
  },
  span: {
    display: 'flex',
    transition: 'transform 150ms ease-out',
    '&.right': {
      transform: 'rotate(-90deg)',
    },
  },
});
